import { Link } from '@tanstack/react-router';
import type { Me, Project } from '@hangar/shared';
import { config } from '@/config';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { AppMark, Logo } from '@/components/Logo';
import { Tag } from '@/components/badges';
import { navGroupsFor, type NavGroup, type NavItem, type ShellView } from './nav';
import { OrgSwitcher } from './OrgSwitcher';
import { ViewSwitch } from './ViewSwitch';
import { ProjectSwitcher } from './ProjectSwitcher';
import { UserCard } from './SidebarUser';
import { NavLink } from './NavLink';

export interface SidebarProps {
  rail: boolean;
  me: Me | undefined;
  meError: boolean;
  projects: Project[] | undefined;
  currentProject: Project | undefined;
  /** Administration or workspace (operators); everyone else is always in the workspace. */
  view: ShellView;
  onSignOut: () => void;
}

function SidebarBrand({ rail }: { rail: boolean }) {
  return (
    <div className={cn('flex h-16 shrink-0 items-center', rail ? 'justify-center' : 'gap-2 px-2')}>
      {rail ? (
        <Link to="/" aria-label="Overview" className="rounded-lg">
          <AppMark size={40} />
        </Link>
      ) : (
        <>
          <Link to="/" aria-label="Overview" className="rounded-lg">
            <Logo height={26} />
          </Link>
          <Tag className="ml-auto" title="Console version">
            v{config.version}
          </Tag>
        </>
      )}
    </div>
  );
}

export function Sidebar({ rail, me, meError, projects, currentProject, view, onSignOut }: SidebarProps) {
  const operator = me?.user?.platformRole === 'operator';
  return (
    // The wrapper stretches with the page so the surface never ends before the content does; the aside sticks.
    <div className={cn('shrink-0 border-r bg-card', rail ? 'w-[72px]' : 'w-60')}>
      <aside className="sticky top-0 flex h-dvh flex-col px-3" aria-label="Sidebar">
        <SidebarBrand rail={rail} />

        <div className="flex flex-col gap-2 py-1">
          {operator ? <ViewSwitch view={view} rail={rail} /> : null}
          <OrgSwitcher me={me} rail={rail} />
          {view === 'workspace' ? <ProjectSwitcher projects={projects} current={currentProject} rail={rail} /> : null}
        </div>

        <nav aria-label="Primary" className="mt-3 flex flex-1 flex-col overflow-y-auto">
          {navGroupsFor(view, me).map((group, i) => (
            <NavGroupBlock key={group.id} group={group} first={i === 0} rail={rail} currentSlug={currentProject?.slug} />
          ))}
        </nav>

        <footer className="mt-2 flex flex-col gap-2 border-t pb-3 pt-3">
          <UserCard me={me} meError={meError} rail={rail} onSignOut={onSignOut} />
        </footer>
      </aside>
    </div>
  );
}

type EntryProps = { item: NavItem; rail: boolean };

/**
 * One group of entries (SPEC decision 30): a quiet kicker above it on the sidebar; on the rail, where there is no room
 * for words, a hairline separates the groups instead. The top group has neither.
 */
function NavGroupBlock({ group, first, rail, currentSlug }: { group: NavGroup; first: boolean; rail: boolean; currentSlug: string | undefined }) {
  const label = group.label;
  return (
    <div role="group" aria-label={label ?? undefined} data-testid={`nav-group-${group.id}`} className="flex flex-col gap-0.5">
      {!first && rail ? <div aria-hidden className="mx-auto my-2 h-px w-8 bg-border" data-testid="nav-group-separator" /> : null}
      {!first && !rail && label ? (
        <div className="px-3 pb-1 pt-5 text-[11px] font-semibold uppercase tracking-[0.08em] text-subtle" data-testid={`nav-group-label-${group.id}`}>
          {label}
        </div>
      ) : null}
      {group.items.map((item) => (
        <NavEntry key={item.slug} item={item} rail={rail} currentSlug={currentSlug} />
      ))}
    </div>
  );
}

function navBase(rail: boolean): string {
  return cn(
    'relative flex items-center rounded-lg text-sm font-medium transition-colors duration-150',
    rail ? 'size-11 justify-center' : 'h-10 gap-3 px-3',
    'before:absolute before:-left-3 before:bottom-2 before:top-2 before:w-[3px] before:rounded-r-full before:bg-primary before:opacity-0 before:transition-opacity before:duration-150',
  );
}

function LiveNavEntry({ item, rail, currentSlug }: EntryProps & { currentSlug: string | undefined }) {
  const active = cn(
    'text-muted-foreground hover:bg-accent hover:text-foreground',
    'aria-[current=page]:bg-accent aria-[current=page]:text-primary aria-[current=page]:before:opacity-100',
    rail && 'aria-[current=page]:border aria-[current=page]:border-primary/30 aria-[current=page]:bg-primary/10',
  );
  return (
    <NavLink slug={item.slug} to={item.href(currentSlug)} data-testid={`nav-${item.slug}`} title={rail ? item.label : undefined} className={cn(navBase(rail), active)}>
      <Icon name={item.icon} size={20} />
      {!rail ? <span className="truncate">{item.label}</span> : null}
    </NavLink>
  );
}

function NavEntry({ item, rail, currentSlug }: EntryProps & { currentSlug: string | undefined }) {
  return <LiveNavEntry item={item} rail={rail} currentSlug={currentSlug} />;
}
